/**
 * API 客户端
 * 与 ValueWarden Worker API 通信
 */

import type {
  RegisterRequest,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  UserProfile,
  PreLoginData,
  SetRescueRequest,
  ResetPasswordRequest,
  AdminUser,
  Cipher,
  Folder,
  CreateCipherRequest,
  UpdateCipherRequest,
  CreateFolderRequest,
  UpdateFolderRequest,
  SyncResponse,
} from '@valuewarden/shared';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    auth = true,
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (auth && this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = data.error || { message: `HTTP ${response.status}`, code: 'UNKNOWN' };
      throw new ApiError(error.message, error.code, response.status, data);
    }

    return data.data as T;
  }

  // ========== 认证相关 ==========

  async preLogin(email: string): Promise<PreLoginData> {
    return this.request<PreLoginData>(`/auth/pre-login?email=${encodeURIComponent(email)}`, {}, false);
  }

  async register(data: RegisterRequest): Promise<{ user: UserProfile }> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false);
  }

  async login(data: LoginRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }, false);
  }

  async refresh(): Promise<RefreshTokenResponse> {
    return this.request<RefreshTokenResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: this.refreshToken }),
    }, false);
  }

  async logout(): Promise<void> {
    await this.request('/auth/logout', { method: 'POST' });
  }

  async me(): Promise<{ user: UserProfile }> {
    return this.request('/auth/me');
  }

  async setRescueCode(data: SetRescueRequest): Promise<{ success: boolean }> {
    return this.request('/auth/set-rescue', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resetPassword(data: ResetPasswordRequest): Promise<{ success: boolean }> {
    return this.request(
      '/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      false,
    );
  }

  // ========== 同步 ==========

  async sync(): Promise<SyncResponse> {
    return this.request<SyncResponse>('/sync');
  }

  // ========== 密码条目 ==========

  async listCiphers(): Promise<{ ciphers: Cipher[] }> {
    return this.request('/ciphers');
  }

  async getCipher(id: string): Promise<{ cipher: Cipher }> {
    return this.request(`/ciphers/${id}`);
  }

  async createCipher(data: CreateCipherRequest): Promise<{ cipher: Cipher }> {
    return this.request('/ciphers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCipher(id: string, data: UpdateCipherRequest): Promise<{ cipher: Cipher }> {
    return this.request(`/ciphers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCipher(id: string): Promise<void> {
    await this.request(`/ciphers/${id}`, { method: 'DELETE' });
  }

  // ========== 文件夹 ==========

  async listFolders(): Promise<{ folders: Folder[] }> {
    return this.request('/folders');
  }

  async createFolder(data: CreateFolderRequest): Promise<{ folder: Folder }> {
    return this.request('/folders', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFolder(id: string, data: UpdateFolderRequest): Promise<{ folder: Folder }> {
    return this.request(`/folders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFolder(id: string): Promise<void> {
    await this.request(`/folders/${id}`, { method: 'DELETE' });
  }

  // ========== 管理后台 ==========

  async listUsers(): Promise<{ users: AdminUser[] }> {
    return this.request('/admin/users');
  }

  async deleteUser(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/users/${id}`, { method: 'DELETE' });
  }

  async revokeUserSessions(id: string): Promise<{ success: boolean; revokedCount: number }> {
    return this.request(`/admin/users/${id}/revoke-sessions`, { method: 'POST' });
  }

  async setUserRole(id: string, isAdmin: boolean): Promise<{ success: boolean }> {
    return this.request(`/admin/users/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ isAdmin }),
    });
  }

  async listInvitations(): Promise<{ invitations: import('@valuewarden/shared').Invitation[] }> {
    return this.request('/admin/invitations');
  }

  async createInvitation(): Promise<import('@valuewarden/shared').CreateInvitationResponse> {
    return this.request('/admin/invitations', { method: 'POST' });
  }

  async revokeInvitation(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/invitations/${id}`, { method: 'DELETE' });
  }
}

export class ApiError extends Error {
  code: string;
  status: number;
  raw: unknown;

  constructor(message: string, code: string, status: number, raw: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.raw = raw;
  }
}

export const api = new ApiClient();
